import React, { useState } from 'react'
import FilterSection from 'src/components/FilterSection/FilterSection'
import FilterSectionCheckboxFilter from 'src/components/FilterSectionCheckboxFilter/FilterSectionCheckboxFilter'
import KeywordFilter from 'src/components/KeywordFilter/KeywordFilter'
import { useStringSetState } from 'src/hooks'
import { useTranslate } from 'src/localization/Trns'
import { UserFeature } from 'src/models/user.models'
import { useQueryUserFeatures } from 'src/queries/auth.queries'
import { css } from 'styled-components'

enum ExpenseCategoryStatus {
  Active = 'Active',
  Inactive = 'Inactive',
}

enum RequiredForTransactionStatus {
  Required = 'Required',
  NotRequired = 'Not Required',
}

enum RequiredForVirtualCardStatus {
  Required = 'Required',
  NotRequired = 'Not Required',
}

interface ExpenseCategoryFilters {
  search?: string
  status?: string[]
  requiredForTransactions?: string[]
  requiredForVirtualCards?: string[]
}

interface ExpenseCategoriesListFilterProps {
  filterValues: ExpenseCategoryFilters
  hasActiveIntegration: boolean
  handleFilterChange: (newFilters: Partial<ExpenseCategoryFilters>) => void
  filterSectionRef: React.MutableRefObject<HTMLDivElement | null>
}

const ExpenseCategoriesListFilter: React.FC<
  ExpenseCategoriesListFilterProps
> = (props): React.ReactElement => {
  const translate = useTranslate()

  const [search, setSearch] = useState<string | undefined>(
    props.filterValues.search,
  )
  const {
    set: statusSet,
    setSet: setStatusSet,
    handleSelection: handleStatusSelection,
  } = useStringSetState(
    new Set<string>(props.filterValues.status) ?? new Set<string>(),
  )

  const {
    set: requiredVirtualCardSet,
    setSet: setRequiredVirtualCardSet,
    handleSelection: handleRequiredForVirtualCardSelection,
  } = useStringSetState(
    new Set<string>(props.filterValues.requiredForVirtualCards) ??
      new Set<string>(),
  )

  const {
    set: requiredTransactionSet,
    setSet: setRequiredTransactionSet,
    handleSelection: handleRequiredForTransactionSelection,
  } = useStringSetState(
    new Set<string>(props.filterValues.requiredForTransactions) ??
      new Set<string>(),
  )

  const { VIEW_VIRTUAL_CARD_FEATURES: hasVirtualCardFeatures } =
    useQueryUserFeatures([UserFeature.VIEW_VIRTUAL_CARD_FEATURES]).data || {}

  const filtersAppliedCount = (): number => {
    let appliedCount = 0
    if (statusSet.size > 0) {
      appliedCount++
    }
    if (requiredVirtualCardSet.size > 0) {
      appliedCount++
    }
    if (requiredTransactionSet.size > 0) {
      appliedCount++
    }
    return appliedCount
  }

  const resetFilters = (): void => {
    setSearch(undefined)
    setStatusSet(new Set())
    setRequiredVirtualCardSet(new Set())
    setRequiredTransactionSet(new Set())
  }

  const filterSearchText = props.hasActiveIntegration
    ? 'Search by category name'
    : 'Search by category name or code'

  return (
    <FilterSection
      css={css`
        width: 100%;
      `}
      innerRef={props.filterSectionRef}
      appliedFiltersCount={filtersAppliedCount()}
      onSave={() => {
        props.handleFilterChange({
          search: search,
          status: Array.from(statusSet),
          requiredForVirtualCards: Array.from(requiredVirtualCardSet),
          requiredForTransactions: Array.from(requiredTransactionSet),
        })
      }}
      filtersFlyoutProps={{
        title: translate('Expense Category Filters'),
        onReset: resetFilters,
        loading: false,
        filters: (
          <>
            <div className="flex-column gap-10">
              <FilterSectionCheckboxFilter
                headerSource={'Expense Category Status'}
                checkboxProps={[
                  {
                    labelSource: 'Active',
                    onChange: (): void => {
                      handleStatusSelection(ExpenseCategoryStatus.Active)
                    },
                    checked: statusSet.has(ExpenseCategoryStatus.Active),
                  },
                  {
                    labelSource: 'Inactive',
                    onChange: (): void => {
                      handleStatusSelection(ExpenseCategoryStatus.Inactive)
                    },
                    checked: statusSet.has(ExpenseCategoryStatus.Inactive),
                  },
                ]}
              />
              {hasVirtualCardFeatures && (
                <FilterSectionCheckboxFilter
                  headerSource={'Required for Virtual Cards Status'}
                  checkboxProps={[
                    {
                      labelSource: 'Required',
                      onChange: (): void => {
                        handleRequiredForVirtualCardSelection(
                          RequiredForVirtualCardStatus.Required,
                        )
                      },
                      checked: requiredVirtualCardSet.has(
                        RequiredForVirtualCardStatus.Required,
                      ),
                    },
                    {
                      labelSource: 'Not Required',
                      onChange: (): void => {
                        handleRequiredForVirtualCardSelection(
                          RequiredForVirtualCardStatus.NotRequired,
                        )
                      },
                      checked: requiredVirtualCardSet.has(
                        RequiredForVirtualCardStatus.NotRequired,
                      ),
                    },
                  ]}
                />
              )}
              <FilterSectionCheckboxFilter
                headerSource={'Required for Transactions Status'}
                checkboxProps={[
                  {
                    labelSource: 'Required',
                    onChange: (): void => {
                      handleRequiredForTransactionSelection(
                        RequiredForTransactionStatus.Required,
                      )
                    },
                    checked: requiredTransactionSet.has(
                      RequiredForTransactionStatus.Required,
                    ),
                  },
                  {
                    labelSource: 'Not Required',
                    onChange: (): void => {
                      handleRequiredForTransactionSelection(
                        RequiredForTransactionStatus.NotRequired,
                      )
                    },
                    checked: requiredTransactionSet.has(
                      RequiredForTransactionStatus.NotRequired,
                    ),
                  },
                ]}
              />
            </div>
          </>
        ),
      }}
      hideButtonChevron
      topLeftContent={
        <div
          css={css`
            width: 100%;
          `}
        >
          <KeywordFilter
            value={search || ''}
            onChange={(val: string): void => {
              setSearch(val)
              props.handleFilterChange({ search: val })
            }}
            placeholder={translate(filterSearchText)}
          />
        </div>
      }
    />
  )
}

export default ExpenseCategoriesListFilter
